import {
  Component,
  useEffect,
  useId,
  useMemo,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from "react";
import { useNavigate } from "react-router";
import {
  hasAnyUserRole,
  hasMePermission,
  hasOrPermission,
  useAppState,
  type MePermissions,
  type UserRole,
} from "@/app";
import {
  AppUpdateBanner,
  Gnb,
  GnbMenu,
  RestrictAccess,
  ServerInfoIndicator,
  SidebarAccountMenu,
} from "@/components/base";
import { ActivityCountBadge } from "@/components/activity";
import { GpuCapacityWidget } from "@/components/gpu";
import { SettingsModal } from "@/components/settings";
// 배럴(@/page/training) 대신 모듈을 직접 import 한다. 배럴은 PageBase 를 상속하는 training
// 페이지를 가져와서 순환 import 가 된다.
import { openTrainingSubmitWindow } from "@/page/training/trainingSubmitWindow";
import { uploadWindows } from "@/page/upload/uploadWindow";
import {
  Scrollbar,
  IconButton,
  Text,
  PanelLeftCloseIcon,
  PanelLeftOpenIcon,
  BrainCircuitIcon,
  CloudUploadIcon,
  SettingsIcon,
} from "@carbon6/c6-frontend-design";
import { HatTextTooltip } from "@/components/tooltip";
import { createLogger } from "@/logging";
import styles from "./PageBase.module.css";
import { hatcheryTextModes } from "@/app/theme";
import { useSidebarAccountData } from "./useSidebarAccountData";

const logger = createLogger("page/base");
const sidebarCollapsedStorageKey = "hatchery-registry:sidebar-collapsed";
const sidebarCollapseMediaQuery = "(min-width: 561px)";

export type PageBaseProps = {
  pageTitle: string;
  // 제목 hover 시 오른쪽에 표시하는 안내 문장. 비우면 아무것도 그리지 않는다.
  pageTitleTooltip?: string;
  currentMenu: GnbMenu;
  showBackButton?: boolean;
  requiredRoles?: readonly UserRole[];
  // 서버가 산출한 permission 게이트(/v1/me). requiredRoles 와 AND 로 적용된다. 플래그가 없으면
  // 본문 대신 RestrictAccess 를 그린다.
  requiredPermission?: keyof MePermissions;
  // role 게이트를 **넓히는** permission. requiredRoles 를 만족하지 않아도 이 플래그가 true 면
  // 본문을 연다. Gnb 항목의 orPermission 과 같은 판정이다. 메뉴가 보이는 사람에게 본문이
  // 잠기면 안 되고, 그 반대도 마찬가지다. permissions 미수신(null)이면 이 축만 닫는다.
  orPermission?: keyof MePermissions;
  scrollable?: boolean;
  tabs?: PageBaseTabs;
  // 탭이 제목 자리를 차지한다. 제목 텍스트 대신 큰 탭 줄을 그리고 아래 탭 줄은 두지 않는다.
  // 제목이 첫 탭과 같은 말을 반복하는 페이지에 쓴다. pageTitle 은 화면에 표시되지 않아도
  // 페이지의 접근 이름으로 남는다.
  tabsReplaceTitle?: boolean;
  // 탭 줄 아래·본문 위에 있는 페이지 슬롯(탭과 콘텐츠 사이). header 안, 본문(tabpanel)
  // 밖이다. 그래서 탭을 전환해도 남는다. 모든 탭에 공통으로 적용하는 조건을 여기에 둔다.
  // 탭 유무와도 관계없다. 비우면 아무것도 그리지 않는다. 인스턴스 상태(컨트롤러·소스)로
  // 내용을 그려야 하는 페이지는 prop 대신 renderBelowTabs(context) 를 오버라이드한다.
  belowTabs?: ReactNode;
  // 제목 텍스트 바로 옆(왼쪽 정렬)의 자리. "이 화면이 무엇을 보고 있는가"를 나타내는 요소를 둔다.
  //   - 목록 페이지: 스코프 컨트롤(My/Team 같은).
  //   - 상세 페이지: 그 대상의 읽기 전용 상태 배지.
  // 목록을 좁히는 축은 필터 행에 둔다. 페이지 단위 **액션**은 headerActions 에 둔다. 누를 수
  // 있는 요소는 오른쪽에, 읽기만 하는 요소는 여기에 둔다.
  // 인스턴스 상태로 그려야 하는 페이지는 prop 대신 renderTitleAside(context) 를 오버라이드한다.
  titleAside?: ReactNode;
  // 제목 우측 끝(탭 위)에 항상 표시하는 페이지 액션. 탭 전환과 관계없이 헤더에 고정된다.
  // 인스턴스 상태(소스·다이얼로그)로 게이팅해야 하는 페이지는 prop 대신
  // renderHeaderActions(context) 를 오버라이드한다.
  headerActions?: ReactNode;
  // 본문(tabpanel) 상단 padding 을 끈다. belowTabs 필터 블록이 이미 헤더·본문 경계를
  // 만드는 페이지가 켠다. 간격 여부는 페이지가 결정한다. PageBase 는 켜고 끄는 prop 만 제공한다.
  bodyFlushTop?: boolean;
};

export type PageBaseTab = {
  value: string;
  label: ReactNode;
  disabled?: boolean;
  requiredRoles?: readonly UserRole[];
};

export type PageBaseTabs = {
  items: readonly PageBaseTab[];
  value?: string;
  defaultValue?: string;
  ariaLabel?: string;
  onValueChange?: (value: string) => void;
};

export type PageBaseContext = {
  currentUserName: string;
  // 현재 사용자 등급(미인증이면 null).
  currentUserRole: UserRole | null;
  // 현재 사용자 uid(미인증이면 null). uid 스코프 localStorage(필터 북마크 등) 키에 쓴다.
  currentUserUid: string | null;
  // 현재 member uuid(미인증/미매핑이면 null). 소유 엔티티 isOwner 판정에 쓴다.
  currentUserMemberUuid: string | null;
  // 서버가 산출한 삭제 권한(미수신이면 null 이다. 소비처가 등급으로 폴백한다).
  currentUserPermissions: MePermissions | null;
  currentTab: string | null;
};

export abstract class PageBase<
  Props extends PageBaseProps = PageBaseProps,
  State = object,
> extends Component<Props, State> {
  componentDidMount(): void {
    // 페이지 진입 직후 필요한 비동기 초기화를 각 페이지가 구현한다.
    void Promise.resolve(this.initPage()).catch((error: unknown) => {
      logger.error("initPage failed.", error);
    });
  }

  componentWillUnmount(): void {
    // 라우트 이탈 시 페이지 단위 구독/캐시를 정리한다.
    void Promise.resolve(this.disposePage()).catch((error: unknown) => {
      logger.error("disposePage failed.", error);
    });
  }

  protected initPage(): void | Promise<void> {
    return undefined;
  }

  protected disposePage(): void | Promise<void> {
    return undefined;
  }

  protected abstract renderContent(context: PageBaseContext): ReactNode;

  // 제목 옆 스코프 슬롯. renderContent 와 같은 컨텍스트 훅이다. 기본은 titleAside prop 을 그대로
  // 쓴다.
  protected renderTitleAside(_context: PageBaseContext): ReactNode {
    return this.props.titleAside ?? null;
  }

  // 제목 우측 헤더 액션. renderContent 와 같은 컨텍스트 훅이다. 기본은 headerActions prop 을
  // 그대로 쓴다. 권한·소스 상태로 게이팅하는 페이지가 오버라이드한다.
  protected renderHeaderActions(_context: PageBaseContext): ReactNode {
    return this.props.headerActions ?? null;
  }

  // 탭 줄 아래 페이지 슬롯. renderHeaderActions 와 같은 컨텍스트 훅이다. 기본은 belowTabs prop 을
  // 그대로 쓴다. 컨트롤러·소스 같은 인스턴스 상태를 그려야 하는 페이지가 오버라이드한다.
  protected renderBelowTabs(_context: PageBaseContext): ReactNode {
    return this.props.belowTabs ?? null;
  }

  render(): ReactNode {
    const {
      bodyFlushTop = false,
      currentMenu,
      pageTitle,
      pageTitleTooltip,
      requiredRoles,
      requiredPermission,
      orPermission,
      scrollable = false,
      showBackButton = false,
      tabs,
      tabsReplaceTitle,
    } = this.props;

    return (
      <PageBaseLayout
        bodyFlushTop={bodyFlushTop}
        currentMenu={currentMenu}
        pageTitle={pageTitle}
        pageTitleTooltip={pageTitleTooltip}
        requiredRoles={requiredRoles}
        requiredPermission={requiredPermission}
        orPermission={orPermission}
        scrollable={scrollable}
        showBackButton={showBackButton}
        tabs={tabs}
        tabsReplaceTitle={tabsReplaceTitle}
        belowTabs={(context) => this.renderBelowTabs(context)}
        titleAside={(context) => this.renderTitleAside(context)}
        headerActions={(context) => this.renderHeaderActions(context)}
      >
        {(context) => this.renderContent(context)}
      </PageBaseLayout>
    );
  }
}

// 레이아웃은 컨텍스트(권한·현재 탭)가 확정된 뒤 헤더 액션·탭 위 슬롯을 평가한다. 그래서 prop
// 노드와 다르게 렌더 시점의 최신 상태를 읽을 수 있다.
type PageBaseLayoutProps = Omit<PageBaseProps, "headerActions" | "belowTabs" | "titleAside"> & {
  belowTabs: (context: PageBaseContext) => ReactNode;
  titleAside: (context: PageBaseContext) => ReactNode;
  headerActions: (context: PageBaseContext) => ReactNode;
  children: (context: PageBaseContext) => ReactNode;
};

function PageBaseLayout({
  bodyFlushTop = false,
  children,
  currentMenu,
  pageTitle,
  pageTitleTooltip,
  requiredRoles,
  requiredPermission,
  orPermission,
  scrollable = false,
  showBackButton,
  tabs,
  tabsReplaceTitle = false,
  belowTabs,
  titleAside,
  headerActions,
}: PageBaseLayoutProps) {
  const { state, endSession } = useAppState();
  const navigate = useNavigate();
  const accountData = useSidebarAccountData();
  const currentUser = state.currentUser;
  const currentUserRole = currentUser?.role ?? null;
  const currentUserPermissions = currentUser?.permissions ?? null;
  const accessibleTabs = useMemo(
    () => createAccessiblePageBaseTabs(tabs, currentUserRole),
    [currentUserRole, tabs],
  );
  // orPermission 은 role 축을 넓힌다(OR). Gnb 항목 게이트와 같은 판정이다. 그래서 메뉴가 보이면
  // 본문도 열린다. requiredPermission 은 AND 축으로 남는다.
  const canAccessPage =
    (hasAnyUserRole(currentUserRole, requiredRoles) ||
      hasOrPermission(currentUserPermissions, orPermission)) &&
    hasMePermission(currentUserPermissions, requiredPermission);
  const [sidebarCollapsed, setSidebarCollapsed] = useSidebarCollapsedPreference();
  // 설정 모달은 사이드바 하단 버튼이 유일한 진입점이다(MCP 연결도 그 안의 한 항목이다).
  const [settingsOpen, setSettingsOpen] = useState(false);
  const sidebarCanCollapse = useSidebarCollapseEnabled();
  const effectiveSidebarCollapsed = sidebarCanCollapse && sidebarCollapsed;
  const tabState = usePageBaseTabs(accessibleTabs);
  const hasTabs = accessibleTabs != null && accessibleTabs.items.length > 0;
  const tabIdPrefix = useId();
  const tabPanelId = `${tabIdPrefix}-panel`;
  const selectedTabIndex =
    hasTabs && tabState.selectedValue != null
      ? accessibleTabs.items.findIndex((tab) => tab.value === tabState.selectedValue)
      : -1;
  const selectedTabId =
    selectedTabIndex >= 0 ? `${tabIdPrefix}-tab-${selectedTabIndex}` : undefined;
  const bodyRole = hasTabs ? "tabpanel" : undefined;
  // 페이지가 본문 상단 여백을 끄면(belowTabs 가 이미 경계를 만드는 화면) flush 클래스를 추가한다.
  const bodyClassName = bodyFlushTop ? `${styles.body} ${styles.bodyFlushTop}` : styles.body;
  // 탭이 있어야 제목을 대신할 수 있다. 탭이 없으면 제목 없는 헤더가 된다. 그래서 기본 모드로
  // 돌아간다.
  const tabsInTitleRow = tabsReplaceTitle && hasTabs;
  // 같은 탭 줄이다. 제목 줄과 그 아래 줄 중 한 곳에만 둔다.
  const tabsNode = hasTabs ? (
    <PageTabs
      tabs={accessibleTabs}
      selectedValue={tabState.selectedValue}
      idPrefix={tabIdPrefix}
      panelId={tabPanelId}
      large={tabsInTitleRow}
      onSelect={tabState.selectValue}
    />
  ) : null;
  const context: PageBaseContext = {
    currentUserName: currentUser?.displayName ?? "사용자",
    currentUserRole,
    currentUserUid: currentUser?.uid ?? null,
    currentUserMemberUuid: currentUser?.memberUuid ?? null,
    currentUserPermissions,
    currentTab: hasTabs ? tabState.selectedValue : null,
  };
  // 접근 불가(403) 화면에는 스코프·페이지 액션·탭 아래 슬롯도 두지 않는다. 이 요소들은 본문과
  // 같은 게이트를 공유한다.
  const titleAsideNode = canAccessPage ? titleAside(context) : null;
  const headerActionsNode = canAccessPage ? headerActions(context) : null;
  const belowTabsNode = canAccessPage ? belowTabs(context) : null;

  function handleSidebarToggle() {
    setSidebarCollapsed((collapsed) => !collapsed);
  }

  function handleLogout() {
    endSession();
    navigate("/", { replace: true });
  }

  // 역할 칩은 admin 일 때만 표시한다. 소속 그룹 요약은 로드가 끝나면 이름들을 합쳐 보여준다.
  const isAdmin = currentUserRole === "admin";
  const accountGroupLabel =
    accountData.groups.status === "ready" && accountData.groups.items.length > 0
      ? accountData.groups.items.map((group) => group.name).join(", ")
      : null;

  // 계정 메뉴.
  //   - 펼침: 브랜드 타이틀 옆 chevron.
  //   - 접힘: 사이드바 맨 아래 Hatchery 로고 트리거.
  const accountMenu = (
    <SidebarAccountMenu
      collapsed={effectiveSidebarCollapsed}
      userName={context.currentUserName}
      adminRoleLabel={isAdmin ? currentUserRole : null}
      groupLabel={accountGroupLabel}
      onOpen={accountData.ensureLoaded}
      onLogout={handleLogout}
    />
  );

  return (
    <main
      className={styles.shell}
      data-sidebar-collapsed={effectiveSidebarCollapsed ? "true" : "false"}
    >
      <aside
        id="page-base-sidebar"
        className={styles.sidebar}
        aria-label="전역 내비게이션"
        data-collapsed={effectiveSidebarCollapsed ? "true" : "false"}
      >
        <div className={styles.sidebarMain}>
          <Gnb
            appName="Hatchery"
            collapsed={effectiveSidebarCollapsed}
            currentMenu={currentMenu}
            currentUserRole={currentUserRole}
            currentUserPermissions={currentUserPermissions}
            // 펼침에서는 타이틀 옆에 계정 chevron 을 둔다. 접힘에서는 아래 sidebarBottom 으로 옮긴다.
            brandMenu={effectiveSidebarCollapsed ? undefined : accountMenu}
            // 접속 환경은 페이지·메뉴가 아니라 앱 전체 컨텍스트다. 그래서 브랜드 바로 아래에 고정한다.
            serverContext={<ServerInfoIndicator collapsed={effectiveSidebarCollapsed} />}
            // 화면과 관계없는 사실은 사이드바 상단에 항상 둔다. 번들이 오래되었다는 안내는
            // 필요할 때만 그린다. 필요 없으면 슬롯은 빈 채로 남는다.
            banner={<AppUpdateBanner collapsed={effectiveSidebarCollapsed} />}
            // 사용자는 진행 중 학습·평가를 그 화면 밖에서도 알아야 한다. 배지가 개수를 항상 표시한다.
            // 두 배지는 폴링 컨트롤러 하나를 공유한다. 그래서 항목이 늘어도 왕복 수는 같다.
            menuAccessories={{
              [GnbMenu.Training]: (
                <ActivityCountBadge axis="training" collapsed={effectiveSidebarCollapsed} />
              ),
              [GnbMenu.TrainingEvaluation]: (
                <ActivityCountBadge axis="evaluation" collapsed={effectiveSidebarCollapsed} />
              ),
              [GnbMenu.TrainingSchedule]: (
                <ActivityCountBadge axis="schedule" collapsed={effectiveSidebarCollapsed} />
              ),
            }}
            shortcuts={
              <>
                <SidebarIconButton
                  label="Dataset 업로드"
                  icon={<CloudUploadIcon />}
                  onClick={() => uploadWindows.open()}
                  large
                />
                <SidebarIconButton
                  label="새 Training"
                  icon={<BrainCircuitIcon />}
                  onClick={() => openTrainingSubmitWindow()}
                  large
                />
              </>
            }
            // 브랜드 행 우측. 사이드바 collapse 토글.
            collapseControl={
              sidebarCanCollapse ? (
                <SidebarIconButton
                  label={effectiveSidebarCollapsed ? "사이드바 펼치기" : "사이드바 접기"}
                  icon={effectiveSidebarCollapsed ? <PanelLeftOpenIcon /> : <PanelLeftCloseIcon />}
                  onClick={handleSidebarToggle}
                  ariaControls="page-base-sidebar"
                  ariaExpanded={!effectiveSidebarCollapsed}
                />
              ) : undefined
            }
          />
        </div>
        {/* 사이드바 맨 아래 고정 영역. 순서는 GPU 가용량 위젯, 설정, 접힘 상태의 계정 트리거다.
            설정은 등급을 검사하지 않는다. 안의 항목(테마·MCP 연결)이 모두 본인 것이다. 관리자
            그룹만 모달이 따로 게이트한다.
            접힘 레일에는 텍스트가 들어가지 않는다. 그래서 같은 자리에 아이콘 버튼을 둔다. */}
        <div className={styles.sidebarBottom}>
          <GpuCapacityWidget collapsed={effectiveSidebarCollapsed} />
          {effectiveSidebarCollapsed ? (
            <SidebarIconButton
              label="설정"
              icon={<SettingsIcon />}
              onClick={() => setSettingsOpen(true)}
            />
          ) : (
            <SidebarTextButton
              icon={<SettingsIcon />}
              label="설정"
              onClick={() => setSettingsOpen(true)}
            />
          )}
          {effectiveSidebarCollapsed ? accountMenu : null}
        </div>
      </aside>
      <section className={styles.content} aria-label={pageTitle}>
        <header className={styles.header}>
          <div className={styles.headerTop}>
            <div className={styles.titleGroup}>
              {showBackButton ? <PageBackButton /> : null}
              {tabsInTitleRow ? (
                tabsNode
              ) : pageTitleTooltip ? (
                // 커스텀 툴팁이 native title 과 겹치지 않게 title 속성을 제거한다.
                <HatTextTooltip text={pageTitleTooltip} placement="right">
                  <Text variant={hatcheryTextModes["head-bold"]}>{pageTitle}</Text>
                </HatTextTooltip>
              ) : (
                <Text variant={hatcheryTextModes["head-bold"]} title={pageTitle}>
                  {pageTitle}
                </Text>
              )}
              {titleAsideNode ? <div className={styles.titleAside}>{titleAsideNode}</div> : null}
            </div>
            {headerActionsNode ? (
              <div className={styles.headerActions}>{headerActionsNode}</div>
            ) : null}
          </div>
          {tabsInTitleRow ? null : tabsNode}
          {belowTabsNode ? <div className={styles.belowTabs}>{belowTabsNode}</div> : null}
        </header>
        {scrollable ? (
          <Scrollbar
            className={bodyClassName}
            data-route-scroll-container="true"
            id={hasTabs ? tabPanelId : undefined}
            role={bodyRole}
            aria-labelledby={selectedTabId}
          >
            {canAccessPage ? children(context) : <RestrictAccess requiredRoles={requiredRoles} />}
          </Scrollbar>
        ) : (
          <div
            className={bodyClassName}
            id={hasTabs ? tabPanelId : undefined}
            role={bodyRole}
            aria-labelledby={selectedTabId}
          >
            {canAccessPage ? children(context) : <RestrictAccess requiredRoles={requiredRoles} />}
          </div>
        )}
      </section>
      <SettingsModal
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        userName={context.currentUserName}
      />
    </main>
  );
}

function createAccessiblePageBaseTabs(
  tabs: PageBaseTabs | undefined,
  userRole: UserRole | null,
): PageBaseTabs | undefined {
  if (tabs == null) {
    return undefined;
  }

  return {
    ...tabs,
    items: tabs.items.filter((tab) => hasAnyUserRole(userRole, tab.requiredRoles)),
  };
}

type PageBaseTabsState = {
  selectedValue: string | null;
  selectValue: (value: string) => void;
};

function usePageBaseTabs(tabs: PageBaseTabs | undefined): PageBaseTabsState {
  const [uncontrolledValue, setUncontrolledValue] = useState<string | null>(() =>
    normalizePageBaseTabValue(tabs, tabs?.defaultValue ?? null),
  );
  const selectedValue = normalizePageBaseTabValue(
    tabs,
    tabs?.value ?? uncontrolledValue ?? tabs?.defaultValue ?? null,
  );

  useEffect(() => {
    if (tabs == null || tabs.value !== undefined || selectedValue === uncontrolledValue) {
      return;
    }

    setUncontrolledValue(selectedValue);
  }, [selectedValue, tabs, uncontrolledValue]);

  function selectValue(value: string) {
    if (tabs == null || !isSelectablePageBaseTabValue(tabs, value) || value === selectedValue) {
      return;
    }

    if (tabs.value === undefined) {
      setUncontrolledValue(value);
    }

    tabs.onValueChange?.(value);
  }

  return { selectedValue, selectValue };
}

function normalizePageBaseTabValue(
  tabs: PageBaseTabs | undefined,
  value: string | null,
): string | null {
  if (tabs == null) {
    return null;
  }

  const enabledItems = tabs.items.filter((tab) => !tab.disabled);
  if (enabledItems.length === 0) {
    return null;
  }

  if (value != null && enabledItems.some((tab) => tab.value === value)) {
    return value;
  }

  return enabledItems[0]?.value ?? null;
}

function isSelectablePageBaseTabValue(tabs: PageBaseTabs, value: string) {
  return tabs.items.some((tab) => tab.value === value && !tab.disabled);
}

type PageTabsProps = {
  tabs: PageBaseTabs;
  selectedValue: string | null;
  idPrefix: string;
  panelId: string;
  // 제목 자리에 있는 탭 줄. 탭이 제목과 같은 크기를 가진다.
  large?: boolean;
  onSelect: (value: string) => void;
};

function PageTabs({ tabs, selectedValue, idPrefix, panelId, large, onSelect }: PageTabsProps) {
  return (
    <div
      className={large ? `${styles.tabs} ${styles.tabsLarge}` : styles.tabs}
      role="tablist"
      aria-label={tabs.ariaLabel ?? "페이지 섹션"}
      tabIndex={-1}
      onKeyDown={(event) => handlePageTabsKeyDown(event, tabs.items, selectedValue, onSelect)}
    >
      {tabs.items.map((tab, index) => {
        const selected = tab.value === selectedValue;

        return (
          <button
            key={tab.value}
            id={`${idPrefix}-tab-${index}`}
            className={styles.tab}
            type="button"
            role="tab"
            aria-controls={panelId}
            aria-selected={selected}
            disabled={tab.disabled}
            tabIndex={selected ? 0 : -1}
            onClick={() => onSelect(tab.value)}
          >
            <span className={styles.tabLabel}>{tab.label}</span>
          </button>
        );
      })}
    </div>
  );
}

function handlePageTabsKeyDown(
  event: KeyboardEvent<HTMLDivElement>,
  items: readonly PageBaseTab[],
  selectedValue: string | null,
  onSelect: (value: string) => void,
) {
  const enabledItems = items.filter((tab) => !tab.disabled);
  if (enabledItems.length === 0) {
    return;
  }

  const selectedIndex = Math.max(
    0,
    enabledItems.findIndex((tab) => tab.value === selectedValue),
  );
  let nextIndex: number;

  switch (event.key) {
    case "ArrowRight":
      nextIndex = (selectedIndex + 1) % enabledItems.length;
      break;
    case "ArrowLeft":
      nextIndex = (selectedIndex - 1 + enabledItems.length) % enabledItems.length;
      break;
    case "Home":
      nextIndex = 0;
      break;
    case "End":
      nextIndex = enabledItems.length - 1;
      break;
    default:
      return;
  }

  event.preventDefault();
  const enabledButtons = Array.from(
    event.currentTarget.querySelectorAll<HTMLButtonElement>('button[role="tab"]:not(:disabled)'),
  );
  enabledButtons[nextIndex]?.focus();
  onSelect(enabledItems[nextIndex].value);
}

function PageBackButton() {
  const navigate = useNavigate();

  return (
    <IconButton
      aria-label="뒤로"
      className={styles.backButton}
      size="md"
      title="이전 페이지로 이동합니다."
      variant="surface"
      onClick={() => navigate(-1)}
    >
      <BackIcon />
    </IconButton>
  );
}

function BackIcon() {
  return (
    <svg className={styles.backIcon} aria-hidden="true" viewBox="0 0 16 16">
      <path d="M9.75 3.5 5.25 8l4.5 4.5" />
      <path d="M5.75 8h7" />
    </svg>
  );
}

// 사이드바 아이콘 버튼 공용. 섹션 액션과 collapse 토글이 사용한다.
// large 는 글리프만 키운다(버튼 박스는 그대로). 섹션 액션처럼 강조해야 하는 진입점에 쓴다.
function SidebarIconButton({
  icon,
  label,
  onClick,
  ariaControls,
  ariaExpanded,
  large = false,
}: {
  icon: ReactNode;
  label: string;
  onClick?: () => void;
  ariaControls?: string;
  ariaExpanded?: boolean;
  large?: boolean;
}) {
  return (
    <HatTextTooltip text={label} placement="right" mode="plain">
      <button
        className={
          large
            ? `${styles.sidebarIconButton} ${styles.sidebarIconButtonLarge}`
            : styles.sidebarIconButton
        }
        type="button"
        aria-label={label}
        aria-controls={ariaControls}
        aria-expanded={ariaExpanded}
        onClick={onClick}
      >
        {icon}
      </button>
    </HatTextTooltip>
  );
}

// 사이드바 텍스트 버튼. 라벨 왼쪽에 아이콘을 둔다. 라벨이 곧 설명이다. 그래서 아이콘 버튼과
// 다르게 툴팁을 붙이지 않는다.
function SidebarTextButton({
  icon,
  label,
  onClick,
}: {
  icon: ReactNode;
  label: string;
  onClick?: () => void;
}) {
  return (
    <button className={styles.sidebarTextButton} type="button" onClick={onClick}>
      <span className={styles.sidebarTextButtonIcon} aria-hidden="true">
        {icon}
      </span>
      {label}
    </button>
  );
}

function useSidebarCollapsedPreference() {
  const [collapsed, setCollapsed] = useState(() => readStoredSidebarCollapsed());

  useEffect(() => {
    writeStoredSidebarCollapsed(collapsed);
  }, [collapsed]);

  return [collapsed, setCollapsed] as const;
}

function useSidebarCollapseEnabled() {
  const [enabled, setEnabled] = useState(() => readSidebarCollapseMediaQuery());

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const mediaQuery = window.matchMedia(sidebarCollapseMediaQuery);
    const handleChange = () => setEnabled(mediaQuery.matches);

    handleChange();
    mediaQuery.addEventListener("change", handleChange);

    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  return enabled;
}

function readSidebarCollapseMediaQuery() {
  if (typeof window === "undefined") {
    return true;
  }

  return window.matchMedia(sidebarCollapseMediaQuery).matches;
}

function readStoredSidebarCollapsed() {
  if (typeof window === "undefined") {
    return false;
  }

  try {
    return window.localStorage.getItem(sidebarCollapsedStorageKey) === "true";
  } catch {
    return false;
  }
}

function writeStoredSidebarCollapsed(collapsed: boolean) {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.setItem(sidebarCollapsedStorageKey, collapsed ? "true" : "false");
  } catch {
    // 사생활 보호 모드나 제한된 브라우저 환경에서는 Storage 를 쓸 수 없을 수 있다.
  }
}
